'use client';

import { Button } from '@/components/button';
import type { StoryblokLinkResource, StoryblokLinkResultResource } from '@/components/customFields/types';
import { trimSlashes } from '@/helper/menuUtils';
import { linkToString } from '@/helper/urls';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';

import classes from './list.module.scss';
import SGListGroup from './listGroup/listGroup';

export interface CustomStoryblokLinkResource extends StoryblokLinkResource {
  children: CustomStoryblokLinkResource[];
}

export default function SGList({ storyList }: { storyList: StoryblokLinkResultResource }) {
  const currentPathName = usePathname(); // e.g., "/guide/item/"

  /**
   * Arrange the stories in ascending order by name and place the start page at the beginning.
   */
  const sortedStoryList = Object.values(storyList).sort((a, b) => {
    if (a.is_startpage && !b.is_startpage) return -1;
    if (!a.is_startpage && b.is_startpage) return 1;
    return a.name.localeCompare(b.name, 'de', { usage: 'sort', sensitivity: 'base' });
  });

  /**
   * This function transforms a flat list of `StoryblokLinkResource` items into a hierarchical tree structure.
   * It organizes the items based on their `parent_id` relationships, grouping children under their respective parents.
   * Items without a valid parent are treated as root-level nodes.
   * @param items
   * @returns
   */
  const buildStoriesHierarchy = (items: StoryblokLinkResource[]): CustomStoryblokLinkResource[] => {
    const itemMap = new Map<number, CustomStoryblokLinkResource>(); // Maps item.id -> item
    const roots: CustomStoryblokLinkResource[] = []; // Holds top-level items (with no parent in the list)

    // Step 1: Initialize each item with a `children` array and store in the map
    for (const item of items) {
      // Store the item by its id for quick access
      itemMap.set(item.id, { ...item, children: [] });
    }

    // Step 2: Build the hierarchy by assigning children to their parents
    for (const item of items) {
      const currentItem = itemMap.get(item.id);

      if (item.parent_id && itemMap.has(item.parent_id)) {
        // If this item's parent exists in the list, push it into parent's children
        itemMap.get(item.parent_id)?.children.push(currentItem!);
      } else {
        // If no parent found (or top-level root), add it to the root list
        roots.push(currentItem!); // root-level item
      }
    }

    return roots;
  };

  const structuredStoryList = buildStoriesHierarchy(sortedStoryList);

  const listTemplate = structuredStoryList.map((story, index) => {
    if (story.is_folder) {
      return <SGListGroup key={story.id} story={story} currentPathName={currentPathName} />;
    } else {
      return (
        <li data-testid={'style-guide-side-navigation-bar-item' + Number(index + 1)} key={story.id}>
          <Button
            variant="text"
            data-testid={'style-guide-side-navigation-bar-item' + Number(index + 1) + '-link'}
            href={linkToString(story.slug)}
            className={classNames(classes.navigationItem, {
              [classes.selected!]: trimSlashes(story.slug) === trimSlashes(currentPathName),
            })}
            aria-current={trimSlashes(story.slug) === trimSlashes(currentPathName)}
          >
            <span className={classes.listItemText}>{story.name}</span>
          </Button>
        </li>
      );
    }
  });

  return (
    <ul aria-label="Navigation" className={classes.styleGuideList} data-testid="style-guide-side-navigation-bar">
      {listTemplate}
    </ul>
  );
}
